import React from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { OperationTypeFilter } from '../gestionarPropiedades/tables/propertiesTable/components/filters/OperationTypeFilter';
import { PropertyTypeFilter } from '../gestionarPropiedades/tables/propertiesTable/components/filters/PropertyTypeFilter';
import { NeighborhoodsFilter } from '../gestionarPropiedades/tables/propertiesTable/components/filters/NeighborhoodsFilter';
import { PriceFilter } from '../gestionarPropiedades/tables/propertiesTable/components/filters/PriceFilter';
import { SurfaceFilter } from '../gestionarPropiedades/tables/propertiesTable/components/filters/SurfaceFilter';
import { CharacteristicsFilter } from '../gestionarPropiedades/tables/propertiesTable/components/filters/CharacteristicsFilter';
import { useState } from 'react';
import { useEffect } from 'react';
import { getNeighborhoods, getPropertiesTypes } from 'redux/propsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { addSearch, getSearches } from 'redux/searchesSlice';
import { AlertFilter } from './AlertFilter';
import AlertInput from './AlertInput';
import { alertTypes, initialFilters } from './alertsModels';
import ModalButtonsContainer from 'sites/common/ModalButtonsContainer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const AlertModal = ({ setModalShow, modalShow }) => {
  // * States
  const [filters, setFilters] = useState(initialFilters);
  const [name, setName] = useState('');

  const neighborhoods = useSelector(state => state.props.neighborhoods)?.map(
    neigh => neigh.value
  );
  const userLogged = useSelector(state => state.login.currentUser);
  const types = useSelector(state => state.props.types);
  const dispatch = useDispatch();

  // * Methods
  const FetchData = async () => {
    await dispatch(getSearches(userLogged.id));
  };

  const HandleSaveSearch = async () => {
    const preferences = {};

    for (let key in filters) {
      const value = filters[key];
      if (!value || (Array.isArray(value) && !value.length)) continue;

      switch (key) {
        case 'operation':
          preferences.operationType = value;
          break;

        case 'propertyTypes':
          preferences.propertyTypes = value;
          break;

        case 'neighborhoods':
          preferences.barrios = value;
          break;

        case 'bedrooms':
          preferences.bedrooms = value;
          break;

        case 'ambiences':
          preferences.ambience = value;
          break;

        case 'bathrooms':
          preferences.bathrooms = Math.max(...value);
          break;

        case 'garages':
          preferences.garage = Math.max(...value);
          break;

        case 'price':
          const { min: minPrice, max: maxPrice, currency } = value;
          if (!minPrice && !maxPrice) break;
          preferences.currency = currency === 'ARS' ? 2 : 1;
          preferences.priceMin = minPrice ? Number(minPrice) : null;
          preferences.priceMax = maxPrice ? Number(maxPrice) : null;
          break;

        case 'surface':
          const { min, max, type } = value;

          if (!min && !max) break;
          preferences.minSurface = min ? Number(min) : null;
          preferences.maxSurface = max ? Number(max) : null;
          preferences.surfaceType = type;
          break;

        default:
          preferences[key] = value;
          break;
      }
    }

    const status = await dispatch(
      addSearch(userLogged.id, {
        preferences,
        name,
        alerts: alertTypes[filters.alerts - 1].type
      })
    );
    if (status < 300) {
      setModalShow(false);
      setFilters(initialFilters);
      setName('');
      FetchData();
    }
  };

  // * Life Cycle
  useEffect(() => {
    getNeighborhoods();
    getPropertiesTypes();
  }, []);

  return (
    <Modal
      show={modalShow}
      onHide={() => setModalShow(false)}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          <FontAwesomeIcon icon="fa-solid fa-bell" />
          &nbsp; Nueva alerta
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column justify-content-center">
          <Row className="g-3">
            <Col xs={12} sm={6} md={6}>
              <AlertInput name={name} setName={setName} />
            </Col>
            <Col xs={12} sm={6} md={6}>
              <AlertFilter filters={filters} setFilters={setFilters} />
            </Col>
            <hr className="mb-0 w-100" />

            <Col xs={12} sm={6} md={6}>
              <OperationTypeFilter
                filtersAndSort={filters}
                setFiltersAndSort={setFilters}
                showAll={false}
              />
            </Col>

            <Col xs={12} sm={6} md={6}>
              <PropertyTypeFilter
                types={types}
                filtersAndSort={filters}
                setFiltersAndSort={setFilters}
              />
            </Col>

            {!!neighborhoods?.length && (
              <Col xs={12} sm={6} md={6}>
                <NeighborhoodsFilter
                  neighborhoods={neighborhoods}
                  filtersAndSort={filters}
                  setFiltersAndSort={setFilters}
                />
              </Col>
            )}

            <hr className="mb-0" />

            <Col xs={12}>
              <PriceFilter
                filtersAndSort={filters}
                setFiltersAndSort={setFilters}
                noApply
              />
            </Col>

            <Col xs={12}>
              <SurfaceFilter
                filtersAndSort={filters}
                setFiltersAndSort={setFilters}
                noApply
              />
            </Col>

            <hr className="mb-0" />

            <Col xs={12} sm={6} md={6}>
              <CharacteristicsFilter
                filtersAndSort={filters}
                setFiltersAndSort={setFilters}
                name="ambiences"
                label="Ambientes"
                singular="ambiente"
                plural="ambientes"
              />
            </Col>

            <Col xs={12} sm={6} md={6}>
              <CharacteristicsFilter
                filtersAndSort={filters}
                setFiltersAndSort={setFilters}
                name="bedrooms"
                label="Habitaciones"
                singular="habitacion"
                plural="habitaciones"
              />
            </Col>

            <Col xs={12} sm={6} md={6}>
              <CharacteristicsFilter
                filtersAndSort={filters}
                setFiltersAndSort={setFilters}
                name="bathrooms"
                label="Baños"
                singular="baño"
                plural="baños"
              />
            </Col>

            <Col xs={12} sm={6} md={6}>
              <CharacteristicsFilter
                filtersAndSort={filters}
                setFiltersAndSort={setFilters}
                name="garages"
                label="Cocheras"
                singular="cochera"
                plural="cocheras"
              />
            </Col>

            <hr className="mb-3" />

            <ModalButtonsContainer>
              <Button
                className="w-100"
                variant="secondary"
                onClick={() => {
                  setFilters(prev => ({
                    ...prev,
                    status: [],
                    property: 0,
                    operation: 0,
                    shared: 'all',
                    price: {
                      min: '',
                      max: '',
                      currency: 'ARS'
                    },
                    surface: {
                      min: '',
                      max: '',
                      type: 'totalSurface'
                    },
                    ambiences: [],
                    bedrooms: [],
                    bathrooms: [],
                    garages: [],
                    neighborhoods: []
                  }));
                }}
              >
                Limpiar filtros
              </Button>
              <Button
                className="w-100"
                variant="success"
                disabled={!name}
                onClick={name ? HandleSaveSearch : () => {}}
              >
                Crear alerta
              </Button>
            </ModalButtonsContainer>
          </Row>
        </div>
      </Modal.Body>
    </Modal>
  );
};

AlertModal.propTypes = {
  modalShow: PropTypes.bool.isRequired,
  setModalShow: PropTypes.func.isRequired
};

export default AlertModal;
